/**
 * The import feature's way of naming a transaction attribute that the
 * super-admin created at RUNTIME.
 *
 * `resolveTransactionAttributeLabel` is the single source of that label, but on
 * its own it can only read the shipped `transactions` JSON catalog, which holds
 * the SEED keys. An attribute created through `/admin/system/attributes` has no
 * JSON entry — the only name it has is the denomination its author typed — so
 * every import surface printed its bare slug. Measured in the browser on
 * 2026-09-22: `loop-interest-expense` in the template-editor preview, while
 * `/admin/system/attribute-mappings` printed «Intereses pagados
 * (loop-interest-expense)» and the customer's Transactions table printed
 * «Intereses pagados». Same attribute, same session, three surfaces, two
 * answers — and the preview is the one that decides whether the admin
 * publishes the mapping.
 *
 * The backend already resolves that chain (denomination → i18n → key) for the
 * requested locale in `TransactionAttributesService.combineWithTranslations`
 * and serves the result as `name` on `/api/transaction-attributes`. The
 * provider reads that catalog — the SAME request the Needs-Attention tray makes,
 * deduped by React Query — and hands the pre-resolved name to the pure
 * resolver. No second resolution chain is written here, which is the point: a
 * second one could diverge, and divergence IS the defect.
 *
 * WHERE THE PROVIDER GOES, AND WHY NOT IN A LAYOUT:
 * mounting it in `AuthenticatedLayout` looked right and was WRONG — measured in
 * the browser on 2026-09-22, the fix did not reach the screen it was written
 * for. The super-admin panel renders under `SuperAdminLayout`, a sibling shell,
 * and the template editor lives there. Putting it in one layout would have
 * repeated the very defect this file closes: a resolver with a surface left
 * out, with a third shell (`OrgAdminLayout`) waiting to repeat it again. It is
 * therefore mounted ONCE in `AppProviders`, above every shell.
 *
 * It sits above them but must not fetch for someone who is not logged in — the
 * login screen would fire an authenticated request and take a 401. Hooks cannot
 * be called conditionally, so the gate is a conditional RENDER: authenticated
 * mounts the fetching inner component, unauthenticated renders the children
 * with no provider at all, which is exactly the degraded path below.
 *
 * WHY A CONTEXT WITH A DEGRADING DEFAULT, AND NOT A FETCHING HOOK PER COMPONENT:
 * the surfaces that need the label are leaves — a preview row, a rule chip, a
 * modal title — rendered in 36 test files that mount them bare, with no
 * QueryClient and with `react-i18next` mocked. Calling a fetching hook in a leaf
 * turned 163 of those green tests red for a reason that has nothing to do with
 * what they assert. Reading a CONTEXT cannot fail: with no provider above it,
 * `useImportAttributeLabel` returns exactly the previous behaviour (JSON, then
 * the raw key), so those tests keep measuring what they were written to measure
 * while the real app — where `AuthenticatedLayout` mounts the provider — gets
 * the catalog name.
 *
 * The same degradation covers the app itself while the catalog request is in
 * flight or if it fails: a slow or broken catalog never blanks a label and
 * never blocks a preview.
 */
import { createContext, createElement, Fragment, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import { useTransactionAttributeCatalog } from '@modules/accounting/features/transactions/hooks/useTransactionAttributes'
import type { TFunction } from '@platform/i18n/hooks/useTranslation'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/shared/contexts/AuthContext'

import { resolveTransactionAttributeLabel } from '../utils/transaction-attribute-label'

/** `(attributeKey) => localized label`. */
export type ImportAttributeLabelResolver = (attributeKey: string) => string

/**
 * `null` means "no catalog above me" — the caller falls back to the JSON-only
 * resolution it used before. It is NOT an error state.
 */
const ImportAttributeLabelContext = createContext<ImportAttributeLabelResolver | null>(null)

/**
 * Fetches the catalog and publishes the resolver. Rendered only when there IS a
 * session — see `ImportAttributeLabelProvider`.
 */
function ImportAttributeLabelCatalog({ children }: { children: ReactNode }) {
  const { t } = useTranslation('imports')
  // `activeOnly: false` (what the catalog hook sets): a preview can legitimately
  // show a row classified with an attribute the super-admin has since
  // deactivated, and that row still deserves its name rather than its slug.
  const { getByKey } = useTransactionAttributeCatalog()

  const resolver = useMemo<ImportAttributeLabelResolver>(
    () => (attributeKey: string) =>
      resolveTransactionAttributeLabel(attributeKey, t, getByKey(attributeKey)?.name),
    [t, getByKey],
  )

  return createElement(ImportAttributeLabelContext.Provider, { value: resolver }, children)
}

/**
 * Makes the live attribute catalog available to every import surface below —
 * customer shell, super-admin panel and org-admin panel alike. Mounted once, in
 * `AppProviders`.
 *
 * Without a session it renders its children untouched: no provider, no request,
 * and every consumer degrades to the JSON-only resolution. That is the whole
 * reason the consumer side reads a context instead of calling a hook.
 */
export function ImportAttributeLabelProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated) {
    return createElement(Fragment, null, children)
  }

  return createElement(ImportAttributeLabelCatalog, null, children)
}

/**
 * The resolver to call from an import surface.
 *
 * @param t the component's own `useTranslation('imports')` translator, used
 *   ONLY for the no-provider fallback. Passing it keeps this hook NON-FETCHING,
 *   which is what lets a bare-rendered component call it safely.
 */
export function useImportAttributeLabel(t: TFunction): ImportAttributeLabelResolver {
  const fromProvider = useContext(ImportAttributeLabelContext)

  const fallback = useCallback<ImportAttributeLabelResolver>(
    (attributeKey: string) => resolveTransactionAttributeLabel(attributeKey, t),
    [t],
  )

  return fromProvider ?? fallback
}
